import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tabs,
  Tab,
  Divider,
  Alert,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";

import { CodingOpContext } from "../../../context/CodingOpContext/CodingOpContext";
import { useObservationsModal } from "../../../context/ObservationsModalContext/ObservationsModalContext";
import { AdvancedMdContext } from "../../../context/AdvancedMD/AdvancedMdContext";

import PatientHeader from "../components/Patient/PatientHeader";
import CodingOpsTable from "../components/Patient/CodingOpsTable";
import LabsPanel from "../components/PatientDetail/tabs/LabsPanel";
import ClinicalNotesTab from "../components/PatientDetail/tabs/ClinicalNotesTab";
import IAAssistTab from "../components/IAAssist/IAAssistTab";

// Helpers
function parseNameFromMemberFullName(memberFullName) {
  if (!memberFullName) return { firstName: "", lastName: "" };
  const parts = String(memberFullName).split(",");
  if (parts.length >= 2) {
    const lastName = (parts[0] || "").trim();
    const firstName = (parts[1] || "").trim().split(" ")[0] || "";
    return { firstName, lastName };
  }
  const tokens = String(memberFullName).trim().split(" ");
  if (tokens.length === 1) return { firstName: tokens[0], lastName: "" };
  return { firstName: tokens[0], lastName: tokens[tokens.length - 1] };
}

function toIsoOrNull(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function normalizeAdvancedMdId(rawId) {
  if (!rawId) return null;
  const s = String(rawId);
  return s.startsWith("pat") ? s.slice(3) : s;
}

const PatientDetailPage = () => {
  const { cipId } = useParams();
  const { showObservationsModal } = useObservationsModal();
  const navigate = useNavigate();

  // AI analysis state (persists when changing tabs)
  const [notesAnalysisPerNote, setNotesAnalysisPerNote] = useState({});
  const [codingOppsAnalysis, setCodingOppsAnalysis] = useState(null);

  const {
    patients,
    codingOps,
    loading,
    error,
    loadCodingOpportunitiesByPatient,
  } = useContext(CodingOpContext);

  const {
    patientLookupResults,
    lookupLoading,
    lookupError,
    matchPatientInAdvancedMd,
    loadLabs,
    loadAnnualWellnessNotes,
    loadFollowUpExamNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
    labs,
    labsLoading,
    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,
    annualWellnessLoading,
    followUpExamLoading,
    telehealthLoading,
    hospitalDischargeLoading,
  } = useContext(AdvancedMdContext);

  const [tab, setTab] = useState(0);

  // Find the patient in the loaded list
  const patient = useMemo(() => {
    return patients?.find((p) => String(p.cipId) === String(cipId)) || null;
  }, [patients, cipId]);

  // Load patient's coding opportunities
  useEffect(() => {
    if (cipId) {
      loadCodingOpportunitiesByPatient(cipId);
    }
  }, [cipId, loadCodingOpportunitiesByPatient]);

  const patientOps = useMemo(() => {
    return (codingOps || []).filter((x) => String(x.cipId) === String(cipId));
  }, [codingOps, cipId]);

  // Match in AdvancedMD
  const matchPayload = useMemo(() => {
    if (!patient) return null;
    const { firstName, lastName } = parseNameFromMemberFullName(patient.memberFullName);
    const dobIso = toIsoOrNull(patient.dateOfBirth);
    const sex = patient.gender || "";
    if (!firstName && !lastName) return null;
    return { firstName, lastName, dob: dobIso, sex };
  }, [patient]);

  useEffect(() => {
    if (matchPayload) {
      matchPatientInAdvancedMd(matchPayload);
    }
  }, [matchPayload, matchPatientInAdvancedMd]);

  const bestMatch = useMemo(() => {
    if (!Array.isArray(patientLookupResults) || patientLookupResults.length === 0) return null;
    return patientLookupResults[0];
  }, [patientLookupResults]);

  const candidate = bestMatch?.candidate || null;

  const advancedMdPatientId = useMemo(() => {
    return normalizeAdvancedMdId(candidate?.id);
  }, [candidate]);

  // Load labs and notes when we have the advancedMdPatientId
  useEffect(() => {
    if (advancedMdPatientId) {
      loadLabs(advancedMdPatientId);
      loadAnnualWellnessNotes(advancedMdPatientId);
      loadFollowUpExamNotes(advancedMdPatientId);
      loadTelehealthNotes(advancedMdPatientId);
      loadHospitalDischargeNotes(advancedMdPatientId);
    }
  }, [
    advancedMdPatientId,
    loadLabs,
    loadAnnualWellnessNotes,
    loadFollowUpExamNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
  ]);

  // Normalize arrays
  const normalizeArray = (x) => {
    const arr = x?.data ?? x;
    return Array.isArray(arr) ? arr : [];
  };

  const labsRows = useMemo(() => normalizeArray(labs), [labs]);

  const notesLoading =
    annualWellnessLoading || followUpExamLoading || telehealthLoading || hospitalDischargeLoading;

  // Organize notes by type
  const notesByType = useMemo(() => {
    return {
      annualWellness: normalizeArray(annualWellnessNotes).map((n) => ({ ...n, __group: "Annual Wellness 2.0" })),
      followUpExam: normalizeArray(followUpExamNotes).map((n) => ({ ...n, __group: "Follow Up Exam 2.0" })),
      telehealth: normalizeArray(telehealthNotes).map((n) => ({ ...n, __group: "TELEHEALTH_2.0" })),
      hospitalDischarge: normalizeArray(hospitalDischargeNotes).map((n) => ({ ...n, __group: "Hospital Discharge Follow Up" })),
    };
  }, [annualWellnessNotes, followUpExamNotes, telehealthNotes, hospitalDischargeNotes]);

  // Todas las notas combinadas
  const allNotes = useMemo(() => {
    const merged = [
      ...notesByType.annualWellness,
      ...notesByType.followUpExam,
      ...notesByType.telehealth,
      ...notesByType.hospitalDischarge,
    ];

    merged.sort((a, b) => {
      const da = new Date(a.noteDateTime || a.noteDate || a.date || "").getTime();
      const db = new Date(b.noteDateTime || b.noteDate || b.date || "").getTime();
      if (Number.isNaN(da) || Number.isNaN(db)) return 0;
      return db - da;
    });

    return merged;
  }, [notesByType]);

  const handleBack = () => {
    navigate("/coding-opportunities");
  };

  if (!patient && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Patient not found. Please go back to the worklist.
        </Alert>
        <Box sx={{ mt: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography component="span" sx={{ ml: 1 }}>
            Back to Worklist
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={handleBack}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Coding Opportunities
        </Link>
        <Typography color="text.primary">Patient Detail</Typography>
      </Breadcrumbs>

      {/* Header del paciente */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <PatientHeader patient={patient} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<NoteAltOutlinedIcon />}
            onClick={() => showObservationsModal(cipId, 7, patient?.memberFullName || "")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              borderColor: "#8B5CF6",
              color: "#7C3AED",
              "&:hover": { bgcolor: "rgba(139, 92, 246, 0.08)", borderColor: "#7C3AED" },
            }}
          >
            Observations
          </Button>
        </Stack>

        {lookupError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            AdvancedMD match issue: {lookupError?.message || "No match found."}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error loading patient data.
          </Alert>
        )}
      </Paper>

      {/* Tabs principales */}
      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`Coding Opportunities (${patientOps.length})`} />
          <Tab label={`Labs (${labsRows.length})`} />
          <Tab label={`Clinical Notes (${allNotes.length})`} />
          <Tab
            label="Coding AI Assist"
            disabled={Object.keys(notesAnalysisPerNote).length === 0}
          />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* Tab 0: Coding Opportunities */}
        {tab === 0 && (
          <CodingOpsTable rows={patientOps} loading={loading} />
        )}

        {/* Tab 1: Labs */}
        {tab === 1 && (
          <LabsPanel labs={labsRows} loading={labsLoading} />
        )}

        {/* Tab 2: Clinical Notes (with AI analysis) */}
        {tab === 2 && (
          <ClinicalNotesTab
            patient={patient}
            notesByType={notesByType}
            loading={notesLoading}
            // Notes analysis state
            notesAnalysisPerNote={notesAnalysisPerNote}
            setNotesAnalysisPerNote={setNotesAnalysisPerNote}
          />
        )}

        {/* Tab 3: Coding AI Assist */}
        {tab === 3 && (
          <IAAssistTab
            patient={patient}
            codingOpportunities={patientOps}
            allNotes={allNotes}
            labs={labsRows}
            loading={labsLoading || notesLoading || lookupLoading}
            advancedMdPatientId={advancedMdPatientId}
            // Analysis state
            notesAnalysisPerNote={notesAnalysisPerNote}
            codingOppsAnalysis={codingOppsAnalysis}
            setCodingOppsAnalysis={setCodingOppsAnalysis}
          />
        )}
      </Paper>
    </Box>
  );
};

export default PatientDetailPage;
